import { IconAlertCircle } from '@developerz/ui';
import { useIsRouting, useLocation, useNavigate } from '@solidjs/router';
import { createEffect, createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { LOGIN_ERROR_PARAM, loginErrorMessage } from '../lib/login-error';
import { Button } from './ui/Button';

// A failed sign-in leg seen by a SIGNED-IN human. The API bounces every leg's
// failure to `/?login_error=<code>`; with a live session the gate renders the
// shell, not <Login/>, so before this the code was dropped on the floor (a
// second account's SSO refused, and nothing said why).
//
// The code is READ ONCE and held: the param is then stripped from the URL
// (replace, so Back does not replay it, and a copied link does not carry it),
// and the held code keeps the notice up until dismissed. The strip waits for the
// router to be IDLE: the bare-path landing (`LegacyRedirect`) is a navigation in
// flight that carries the search onto `/org/<login>/…`, and a strip issued
// during it replaced it, leaving the person on the bare path. Once idle, the
// effect re-runs on the landed location and strips there. No fetch, so no
// container/presenter split: the URL is the only input.

export function LoginErrorNotice(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const isRouting = useIsRouting();
  const [code, setCode] = createSignal<string | null>(null);

  createEffect(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get(LOGIN_ERROR_PARAM);
    if (raw === null) return;
    setCode((held) => held ?? raw);
    if (isRouting()) return;
    params.delete(LOGIN_ERROR_PARAM);
    const search = params.toString();
    navigate(`${location.pathname}${search === '' ? '' : `?${search}`}${location.hash}`, {
      replace: true,
      scroll: false,
    });
  });

  return (
    <Show when={code()} keyed>
      {(c) => (
        <div class="alert alert--destructive alert--icon" role="alert">
          <IconAlertCircle />
          <p class="alert__title">{loginErrorMessage(c)}</p>
          <Button variant="ghost" size="sm" onClick={() => setCode(null)}>
            {t('login.error_dismiss')}
          </Button>
        </div>
      )}
    </Show>
  );
}
